import React, { useState, useEffect } from 'react';
import LeftMenu from '../components/LeftMenu';
import withAuth from '../common/auth';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Slide } from 'react-toastify';
import Swal from 'sweetalert2';
import AddDepartmentModal from '../components/AddDepartmentModal';
import axios from 'axios';
import { GET_MASTER_DATA, BASE_URL } from '../utlilities/config';

// Icons
import { 
  FiSearch, FiPlus, FiTrash2, FiRefreshCw, 
  FiEdit, FiCheckCircle,
  FiFileText, FiAlertCircle
} from "react-icons/fi";
import { FaBuilding } from "react-icons/fa";

// No Data component
const NoData = () => (
  <div className="flex flex-col items-center justify-center h-64 py-10">
    <div className="w-20 h-20 mb-4 bg-gray-100 rounded-full flex items-center justify-center text-gray-400">
      <FiFileText size={32} />
    </div>
    <h3 className="text-lg font-medium text-gray-700">No departments found</h3>
    <p className="text-sm text-black mt-1 mb-6">Try adjusting your search or add a new department</p>
  </div>
);

const Spinner = () => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-600 font-medium">Loading lab data...</p>
    </div>
  </div>
);


const DepartmentMaster = () => {
  const token = localStorage.getItem('accessToken');
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [hoverRow, setHoverRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [departmentName, setDepartmentName] = useState('');
  const [departmentId, setDepartmentId] = useState(null);

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}${GET_MASTER_DATA}`, {
        headers: {
          'XApiKey': process.env.REACT_APP_API_KEY,
          'Authorization': `Bearer ${token}`
        },
      });

      if (response.status === 200) {
        setLoading(false);
        console.log('response',response.data.data);
        setDepartments((response.data.data || []).filter(item => item.Type === 'D'));
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Failed to fetch labs data:', error);
      toast.error('Could not load labs data', {
        autoClose: 1000,
        icon: <FiAlertCircle className="text-red-500" />
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = () => {
    setIsMenuExpanded(!isMenuExpanded);
  };

  const handleClick = () => {
    setShowModal(true);
  };

  const handleEditClick = (id, name) => {
    setDepartmentName(name);
    setDepartmentId(id);
    setShowModal(true);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDeleteClick = (id) => {
    Swal.fire({
      title: 'Delete Department?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#d1d5db',
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      customClass: {
        confirmButton: 'rounded-lg text-sm font-medium px-5 py-2.5',
        cancelButton: 'rounded-lg text-sm font-medium px-5 py-2.5 mr-2',
        popup: 'rounded-xl shadow-xl'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        setDepartments(departments.filter(d => d.id !== id));
        toast.success('Department successfully deleted.', {
          autoClose: 1000,
          icon: <FiCheckCircle className="text-green-500" />
        });
      }
    });
  };

  const handleRefresh = () => {
    getData();
  };

  const handleModalClose = () => {
    setShowModal(false);
    setDepartmentName('');
    setDepartmentId(null);
  };

  // Filter data based on search query
  const filteredDepartments = departments.filter(department => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      department.Name?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="flex h-full bg-background">
      {loading && <Spinner />}
      <ToastContainer
        position="top-center"
        autoClose={1000}
        limit={3}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />
      
      {/* <LeftMenu isExpanded={isMenuExpanded} toggleMenu={toggleMenu} /> */}
      
      <div className="p-6 flex-1 flex flex-col overflow-hidden">
        {/* Page Header */}
        <div className="bg-white rounded-xl shadow-md p-3 mb-3">
          <div className="flex justify-between items-center">
            <h1 className="text-lg font-bold text-gray-800 flex items-center">
              Department Master
            </h1>
            
            <button 
              className="px-4 py-1.5 text-sm font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center"
              onClick={handleClick}
            >
              <FiPlus className="mr-2" /> Add New Department
            </button>
          </div>
          
        </div>
        
        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-md p-5 mb-6">
          <div className="flex flex-wrap justify-between items-center">
            {/* Search */}
            <div className="relative w-full md:w-72">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                <FiSearch size={16} />
              </div>
              <input
                type="text"
                placeholder="Search by department name"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
        </div>
        
        {/* Results Count */}
        <div className="flex justify-between items-center mb-3">
          <div className="text-sm text-black">
            {filteredDepartments.length} 
            {filteredDepartments.length === 1 ? ' department' : ' departments'} found
            {searchQuery && ` for "${searchQuery}"`}
          </div>
          
          <button 
            onClick={handleRefresh}
            className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
          >
            <FiRefreshCw className="mr-1" size={14} /> Refresh
          </button>
        </div>
        
        {/* Table */}
        <div className="bg-white rounded-xl shadow-md flex-1 overflow-hidden">
          {filteredDepartments.length > 0 ? (
            <div className="overflow-x-auto h-full">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">
                      Department Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-black uppercase tracking-wider border-b">
                      Actions
                    </th>
                  </tr>
                </thead>
                
                <tbody className="divide-y divide-gray-100">
                  {filteredDepartments.map((department, index) => (
                    <tr 
                      key={department.ID} 
                      className={`${
                        hoverRow === index ? 'bg-indigo-50' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      } transition-colors hover:bg-indigo-50`}
                      onMouseEnter={() => setHoverRow(index)}
                      onMouseLeave={() => setHoverRow(null)}
                    >
                      <td className="px-6 py-2 text-sm text-black whitespace-nowrap">
                        {department.Name}
                      </td>
                      
                      <td className="px-6 py-2 text-sm text-black whitespace-nowrap">
                        <div className="flex space-x-3">
                          <button
                            className="p-1.5 bg-indigo-50 text-indigo-600 rounded-md hover:bg-indigo-100 transition-colors"
                            onClick={() => handleEditClick(department.ID, department.Name)}
                            title="Edit department"
                          >
                            <FiEdit size={16} />
                          </button>
                          
                          <button
                            className="p-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 transition-colors"
                            onClick={() => handleDeleteClick(department.id)}
                            title="Delete department"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoData />
          )}
        </div>
      </div>
      
      {/* Modal */}
      {showModal && (
        <AddDepartmentModal 
          setShowModal={handleModalClose} 
          departmentName={departmentName}
          onSave={handleRefresh} 
          departmentId={departmentId}
          setSelectedDepartmentId={setDepartmentId}
          setDepartmentName={setDepartmentName}
        />
      )}
    </div>
  );
};

export default withAuth(DepartmentMaster);

